import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { sortBy } from 'lodash';
import moment from 'moment-timezone';
import { groupBy, map, uniq } from 'lodash';
import BaseButton from '@magoosh/lib/button/base';
import { toSentence } from 'utilities/formatting';
import { formatTimeRange, longTimeZoneName } from 'utilities/time';
import style from './style.module.scss';
import colors from '@magoosh/lib/styles/colors.module.scss';

interface Cohort {
  sessions: {
    startTime: string;
    endTime: string;
    name: string;
    description: string;
  }[];
  onDemand: boolean;
  purchaseUrl: string;
}

interface Props {
  cohorts: Cohort[];
  coupon: string;
}

const timeZone = moment.tz.guess();

// This is a widget and likely shown in an iframe. Let the parent know about component updates
// so the iframe can adjust its size accordingly.
const notifyParentOfContentChange = () => {
  setTimeout(() => {
    window.parent.postMessage(
      {
        event: 'live_class_listing.reload',
        height: `${window.document.body.scrollHeight}px`
      },
      '*'
    );
  }, 10); // Slight delay was needed during testing for the resize to happen
};

const LiveClassPrepurchaseWidget: React.FC<Props> = (props) => {
  // Make sure the iframe's initial height gets set correctly
  useEffect(() => {
    notifyParentOfContentChange();
  }, []);

  return (
    <div className="bg-white">
      <div className="u-padding-A-s">
        Showing class times in your timezone:{' '}
        <span className="text-underdash">{longTimeZoneName(moment().tz(timeZone))}</span>
      </div>

      {props.cohorts.map((cohort, idx) => (
        <CohortRow key={idx} cohort={cohort} coupon={props.coupon} />
      ))}
    </div>
  );
};

interface CohortRowProps {
  cohort: Cohort;
  coupon: string;
}

const CohortRow: React.FC<CohortRowProps> = (props) => {
  const { cohort, coupon } = props;
  const { sessions, purchaseUrl } = cohort;
  const [showClassDetails, setShowClassDetails] = useState(false);

  const sortedSessions = sortBy(sessions, [
    (session) => {
      if (cohort.onDemand) {
        return session.name;
      } else {
        return session.startTime;
      }
    }
  ]);

  // ['Monday 5pm-7pm', 'Wednesday 5pm-7pm', ...]
  // Hard limit at 2 for now (which matches all our offerings) because daylight savings
  // cross-over results in some weird behavior for non-US timezones.
  const sessionDaysOfWeekAndTimes = uniq(
    sortedSessions.map((session) => {
      const startTime = moment(session.startTime).tz(timeZone);
      const endTime = moment(session.endTime).tz(timeZone);
      return formatTimeRange(startTime, endTime, 'dddd ');
    })
  );

  const handleClassDetailsToggled = () => {
    setShowClassDetails(!showClassDetails);
    notifyParentOfContentChange();
  };

  const purchaseUrlWithParams = () => {
    let urlParams = new URLSearchParams({
      show_cohort_options: 'true'
    });

    if (coupon) {
      urlParams.append('coupon', coupon);
    }

    return `${purchaseUrl}?${urlParams.toString()}`;
  };

  return (
    <div className="u-padding-A-s u-border-A-s u-margin-A-s">
      <div className="row">
        <div className={cx(style.cohortCol, 'col-sm-3')}>
          <div className="d-flex align-items-center">
            <div>
              <h2 className="u-margin-A-n" style={{ color: colors.brandRadRed }}>
                <strong>
                  {cohort.onDemand ? '***' : moment(sortedSessions[0].startTime).tz(timeZone).format('DD')}
                </strong>
              </h2>
            </div>

            <div className="u-margin-L-s">
              <div>
                <strong>
                  {cohort.onDemand
                    ? 'On Demand'
                    : moment(sortedSessions[0].startTime).tz(timeZone).format('MMMM')}
                </strong>
              </div>
              <div className="text-gray-light small">{sortedSessions.length} classes</div>
            </div>
          </div>
        </div>

        <div className={cx(style.cohortCol, 'col-sm-6')}>
          <div>
            <strong>
              {cohort.onDemand
                ? 'Watch classes at your own pace and time'
                : toSentence(sessionDaysOfWeekAndTimes)}
            </strong>
          </div>

          {!cohort.onDemand &&
            `${moment(sortedSessions[0].startTime).tz(timeZone).format('MMMM D')} - ${moment(
              sortedSessions[sortedSessions.length - 1].startTime
            )
              .tz(timeZone)
              .format('MMMM D')}`}
          <span className="hidden-xs">
            {!cohort.onDemand && <span className="u-margin-H-s">•</span>}
            <a className="text-gray-light small" onClick={handleClassDetailsToggled}>
              Detailed Class Schedule
            </a>
          </span>
        </div>

        <div className={cx(style.cohortCol, 'col-sm-3')}>
          <div className={style.cohortCTA}>
            <BaseButton href={purchaseUrlWithParams()} className={style.radRedButton} target="_parent">
              Sign Up Now
            </BaseButton>
          </div>
        </div>
      </div>

      {showClassDetails && (
        <div className="row">
          <div className="col-xs-12">
            {sortedSessions.map((session, idx) => {
              const startTime = moment(session.startTime).tz(timeZone);
              const endTime = moment(session.endTime).tz(timeZone);

              return (
                <div key={idx}>
                  <hr />
                  <div className="d-flex flex-row justify-content-between">
                    <div className="u-margin-R-m" style={{ minWidth: 120 }}>
                      {!cohort.onDemand && (
                        <div
                          className="small"
                          dangerouslySetInnerHTML={{
                            __html: formatTimeRange(startTime, endTime, 'ddd, MMM. D<br />')
                          }}
                        />
                      )}
                    </div>

                    <div style={{ flexGrow: 2 }}>
                      <strong>{session.name}</strong>
                      <p>{session.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveClassPrepurchaseWidget;
